// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { isAxiosError } from "axios"
import {
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query"
import { apiClient } from "@/queries/client"
import { pollingOptionsFor } from "@/lib/refresh-policy"
import { cachedCycleStatus } from "@/queries/queries.evolutionCycles"

// TanStack hooks for the three `/front-api/event-journal/*` read
// verbs shipped by slice 05 (PRD §Modules M4) — consumed by the cycle
// dashboard (PRD §Modules M9, slices 07–11).
//
// The hooks ship together:
//   - `useCycleEvents({ cycleId, mode?, offset?, limit?, names? })` —
//     the metadata-only feed (INV-5). The single hook serves both the
//     names-only read and the events panel's paginated, relevance-aware
//     read (mode + offset + limit); pagination args are optional, so a
//     bare `{ cycleId }` call is the legacy unpaged shape.
//   - `useEventById(eventId)` — on-demand row payload fetch ; consumed
//     by slice 11's click-to-expand action.
//   - `useCycleLatestSnapshot(cycleId)` — `EventDetails` envelope with
//     a verbatim `CycleSnapshotDto` JSON string inside `payload_json`;
//     consumed by slices 08–10 (cost / fitness / leaderboard panels).
//
// Every hook routes through the shared `apiClient` (single instance,
// request-interceptor auth, central 401 redirect — ADR-0011). The
// wire is snake_case (PRD §Invariants INV-6); the matching TS shapes
// live in `types/event-journal.d.ts`.
//
// `refetchOnWindowFocus: true` is set at the hook level so the four
// downstream chart slices inherit US §11 ("fresh data on tab revisit")
// automatically — they consume the hooks, not their config.
//
// Cache keys embed every input that changes the wire response so
// variant calls do not collide. The `names` filter is cached by its
// canonical sorted form (`[...names].sort()`) so caller call-site
// ordering does not fragment the cache.

// Relevance-aware feed mode for the events list (ADR-0049). `relevant`
// is the Backend default (lifecycle milestones + always-on failures);
// `all` opts into the full firehose.
export type EventFeedMode = "relevant" | "all"

export interface CycleEventsParams {
  cycleId: string | undefined
  // Omit for the Backend default (`relevant`).
  mode?: EventFeedMode
  // Omit for an unpaged read; set together with `limit` to page.
  offset?: number
  limit?: number
  // Filter to these event names (comma-joined server-side).
  names?: ReadonlyArray<string>
}

// GET /front-api/event-journal/cycles/{cycleId}/events
//     ?mode=relevant|all&offset=<int>&limit=<int>&names=A,B
//
// Returns the metadata-only summaries (no payload — INV-5) ordered
// `OccurredAt DESC` by the Backend, after applying the relevance filter
// (so a page of N rows is N events that pass the active `mode`). Failure
// / error events (`*Failed` / `*Exhausted`) are always present in the
// relevant view — guaranteed Backend-side, never re-derived here.
//
// `mode` / `offset` / `limit` / `names` are all optional and sent only
// when provided, so `{ cycleId }` is the legacy names-only unpaged read
// and `{ cycleId, mode, offset, limit }` is the panel's paged read — one
// hook, both shapes. The response always carries the page envelope
// `{ items, total, offset, limit }`, degraded from the row count when the
// Backend omits a field (partial / legacy response).
//
// Disabled until `cycleId` is a non-empty string (defensive against
// route params not yet resolved on first render).
export const useCycleEvents = ({
  cycleId,
  mode,
  offset,
  limit,
  names,
}: CycleEventsParams): UseQueryResult<EventListResponse, Error> => {
  const queryClient = useQueryClient()
  // Canonical sort so `["A","B"]` and `["B","A"]` map to the same key.
  const sortedNames = names ? [...names].sort() : undefined
  return useQuery<EventListResponse, Error>({
    queryKey: [
      "eventJournal",
      "events",
      cycleId,
      mode ?? null,
      offset ?? null,
      limit ?? null,
      sortedNames ?? null,
    ],
    enabled: typeof cycleId === "string" && cycleId.length > 0,
    refetchOnWindowFocus: true,
    // Slice `refresh-policy-live-polling`: the `events` kind reads its
    // polling status from the `detail` query's cache entry — see
    // `evolutionCycleDetailKey`.
    ...pollingOptionsFor("events", cachedCycleStatus(queryClient, cycleId)),
    queryFn: async () => {
      const url = `/front-api/event-journal/cycles/${encodeURIComponent(
        cycleId as string,
      )}/events`
      const params: Record<string, string> = {}
      if (mode) params.mode = mode
      if (offset !== undefined) params.offset = String(offset)
      if (limit !== undefined) params.limit = String(limit)
      // The Backend's filter takes a comma-separated `names=A,B` query
      // string (cf. EventJournalFrontApiEndpoints.ParseNamesFilter).
      if (sortedNames && sortedNames.length > 0) {
        params.names = sortedNames.join(",")
      }
      const response = await apiClient.get<Partial<EventListResponse>>(url, {
        params,
      })
      const data = response.data ?? {}
      const items = data.items ?? []
      // The Backend always returns the page envelope, but degrade
      // gracefully if a field is absent so consumers never crash on a
      // partial / legacy response.
      return {
        items,
        total: data.total ?? items.length,
        offset: data.offset ?? offset ?? 0,
        limit: data.limit ?? limit ?? items.length,
      }
    },
  })
}

// Cache key for a single event's on-demand payload fetch — the one
// place `useEventById` and any other bounded per-event reader (e.g.
// `CycleHealth`'s per-failure-line enrichment) key their cache entry,
// so a row already fetched by one consumer is never re-fetched by the
// other.
export const eventByIdKey = (eventId: string) =>
  ["eventJournal", "event", eventId] as const

// GET /front-api/event-journal/events/{eventId} — non-hook fetch shared
// by `useEventById` and any `useQueries`-based caller that needs the
// same URL + envelope handling outside a single `useQuery` hook.
export const fetchEventById = async (eventId: string): Promise<EventDetails> => {
  const response = await apiClient.get<EventDetails>(
    `/front-api/event-journal/events/${encodeURIComponent(eventId)}`,
  )
  return response.data
}

// On-demand per-event fetch — used by the events panel's click-to-
// expand interaction (slice 11). The Backend returns the full
// `EventDetails` envelope including the verbatim `payload_json`
// string; the caller parses it client-side (INV-4).
//
// Disabled until `eventId` is a non-empty string. The events panel
// passes `undefined` until a row is clicked, so the disabled gate is
// the canonical "lazy" pattern.
export const useEventById = (
  eventId: string | undefined,
): UseQueryResult<EventDetails, Error> => {
  return useQuery<EventDetails, Error>({
    queryKey: eventByIdKey(eventId as string),
    enabled: typeof eventId === "string" && eventId.length > 0,
    refetchOnWindowFocus: false, // event payload is immutable — no refresh
    queryFn: () => fetchEventById(eventId as string),
  })
}

// GET /front-api/event-journal/cycles/{cycleId}/latest-snapshot
//
// Hot-path read for the dashboard's four chart panels (slices 08–10)
// and the leaderboard. The Backend returns the most-recent
// `EvolutionCycleSnapshotEvent`'s `EventDetails` envelope, or 404
// when no snapshot exists yet (used by the dashboard shell to render
// the US §14 error state).
//
// `refetchOnWindowFocus: true` (US §11) — tab revisit fetches a
// fresher cumulative snapshot. The hook does not throw on 404; the
// `isError` branch is the seam the shell branches on.
export const useCycleLatestSnapshot = (
  cycleId: string | undefined,
): UseQueryResult<EventDetails | null, Error> => {
  const queryClient = useQueryClient()
  return useQuery<EventDetails | null, Error>({
    queryKey: ["eventJournal", "latest-snapshot", cycleId],
    enabled: typeof cycleId === "string" && cycleId.length > 0,
    refetchOnWindowFocus: true,
    // Slice `refresh-policy-live-polling`: the `snapshot` kind reads
    // its polling status from the `detail` query's cache entry — see
    // `evolutionCycleDetailKey`.
    ...pollingOptionsFor("snapshot", cachedCycleStatus(queryClient, cycleId)),
    queryFn: async () => {
      try {
        const response = await apiClient.get<EventDetails>(
          `/front-api/event-journal/cycles/${encodeURIComponent(
            cycleId as string,
          )}/latest-snapshot`,
        )
        return response.data
      } catch (error) {
        // No snapshot yet is the normal state for the whole first
        // iteration — the Backend answers 404 until the first milestone
        // lands. Left to throw, the query never leaves `pending`, so
        // `isLoading` flapped true on every retry and every 5 s poll and
        // the panels that compose it (`CycleHealth`, `EvolverProgressPanel`)
        // oscillated between "Loading panel…" and their degraded render.
        // Absent data is data: resolve to `null`, exactly like the
        // `latest-progress` twin below. Every consumer already gates on
        // `isSuccess && data != null`, so the degraded branch is unchanged.
        if (isAxiosError(error) && error.response?.status === 404) {
          return null
        }
        throw error
      }
    },
  })
}

// `GET /front-api/event-journal/cycles/{cycleId}/latest-progress`
//
// Polled read behind the dashboard's accumulating-tier values (budget
// ring, tokens, burn, ETA, agent counter — PRD §Modules
// DashboardPerPanelGating). The Backend stores the progress row
// latest-wins (ADR-0018 latest-by-name) and returns the single surviving
// `EventDetails` envelope with a verbatim `CycleProgressDto`
// `payload_json` string, or 404 before the first tick lands.
//
// Unlike `useCycleLatestSnapshot`, a 404 is an *absent-data* state, not
// an error: "no progress row yet" is the normal pre-first-tick condition
// on a healthy cycle, so the queryFn maps it to `null` and the dashboard
// renders a zero-state instead of an error alert (AC3). Any other status
// still rejects and surfaces via `isError`.
//
// Same refresh policy as the snapshot read: 5 s while the cached detail
// status is non-terminal, off on terminal (INV — polling stops on
// terminal status).
export const useCycleLatestProgress = (
  cycleId: string | undefined,
): UseQueryResult<EventDetails | null, Error> => {
  const queryClient = useQueryClient()
  return useQuery<EventDetails | null, Error>({
    queryKey: ["eventJournal", "latest-progress", cycleId],
    enabled: typeof cycleId === "string" && cycleId.length > 0,
    refetchOnWindowFocus: true,
    ...pollingOptionsFor("snapshot", cachedCycleStatus(queryClient, cycleId)),
    queryFn: async () => {
      try {
        const response = await apiClient.get<EventDetails>(
          `/front-api/event-journal/cycles/${encodeURIComponent(
            cycleId as string,
          )}/latest-progress`,
        )
        return response.data
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 404) {
          return null
        }
        throw error
      }
    },
  })
}
